import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  getPaymentStats, 
  getAllTransactions, 
  getAllDiscountCodes, 
  createDiscountCode, 
  deleteDiscountCode,
  updateTierPricing,
  getPaymentTiers,
  getAdminTiers,
  startPaypalReconcile,
  getPaypalReconcileStatus
} from '../services/api';
import { 
  DollarSign, 
  Percent, 
  Tag, 
  Plus, 
  Trash2, 
  TrendingUp, 
  Users, 
  CreditCard,
  Gift,
  Loader2,
  ArrowLeft,
  RefreshCw,
  Edit3,
  Save,
  X,
  Check
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const PaymentAdminPage = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Tier editing state
  const [editingTier, setEditingTier] = useState(null);
  const [tierEdits, setTierEdits] = useState({});
  const [savingTier, setSavingTier] = useState(false);
  const [paypalSyncing, setPaypalSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');

  const pollReconcile = async () => {
    for (let i = 0; i < 120; i++) {
      await new Promise(r => setTimeout(r, 4000));
      const report = await getPaypalReconcileStatus();
      if (report.status === 'running') {
        setSyncProgress(`Checking ${report.checked || 0}/${report.total || '?'} pending subscriptions...`);
      } else {
        return report;
      }
    }
    throw new Error('Reconcile timed out');
  };

  const handlePaypalSync = async () => {
    setPaypalSyncing(true);
    setSyncProgress('Scanning pending PayPal subscriptions...');
    try {
      await startPaypalReconcile(true);
      const dryReport = await pollReconcile();
      if (dryReport.status !== 'done') throw new Error(dryReport.error || 'Scan failed');
      const found = dryReport.active_found || [];
      if (found.length === 0) {
        toast({ title: 'PayPal Sync Complete', description: `Checked ${dryReport.checked} pending subscriptions — none are active on PayPal.` });
      } else {
        const names = found.map(f => `${f.email} (${f.tier})`).join('\n');
        if (window.confirm(`Found ${found.length} member(s) actively paying on PayPal but not linked in the app:\n\n${names}\n\nRe-link them now? (No emails will be sent)`)) {
          setSyncProgress('Re-linking members...');
          await startPaypalReconcile(false);
          const applyReport = await pollReconcile();
          if (applyReport.status !== 'done') throw new Error(applyReport.error || 'Re-link failed');
          toast({ title: 'PayPal Sync Complete', description: `Re-linked ${applyReport.relinked_count} member(s) to their active PayPal subscriptions.` });
          loadData();
        }
      }
    } catch (error) {
      toast({ title: 'PayPal Sync Failed', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally {
      setPaypalSyncing(false);
      setSyncProgress('');
    }
  };
  
  // New discount form
  const [newDiscount, setNewDiscount] = useState({
    code: '',
    discount_percent: '',
    discount_amount: '',
    discountType: 'percent',
    applicable_tiers: ['bronze', 'silver', 'gold'],
    max_uses: '',
    valid_until: '',
    new_members_only: false,
    monthly_only: false
  });
  const [creatingDiscount, setCreatingDiscount] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsData, transactionsData, discountsData, tiersData] = await Promise.all([
        getPaymentStats(),
        getAllTransactions(),
        getAllDiscountCodes(),
        getAdminTiers().catch(() => getPaymentTiers())
      ]);
      setStats(statsData);
      setTransactions(transactionsData);
      setDiscountCodes(discountsData);
      setTiers(tiersData.tiers || {});
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load payment data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) {
      loadData();
    }
  }, [isOwner, loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast({
      title: 'Refreshed',
      description: 'Payment data updated'
    });
  };

  // Tier editing functions
  const startEditingTier = (tierId) => {
    const tier = tiers[tierId];
    setEditingTier(tierId);
    setTierEdits({
      name: tier.name || '',
      price: tier.price || 0,
      description: tier.description || '',
      benefits: (tier.benefits || []).join('\n')
    });
  };

  const cancelEditingTier = () => {
    setEditingTier(null);
    setTierEdits({});
  };

  const handleSaveTier = async () => {
    if (!editingTier) return;

    try {
      setSavingTier(true);
      
      const updateData = {
        name: tierEdits.name,
        price: parseFloat(tierEdits.price),
        description: tierEdits.description,
        benefits: tierEdits.benefits.split('\n').filter(b => b.trim())
      };

      const result = await updateTierPricing(editingTier, updateData);
      
      // Update local state
      setTiers(prev => ({
        ...prev,
        [editingTier]: {
          ...prev[editingTier],
          ...updateData
        }
      }));
      
      setEditingTier(null);
      setTierEdits({});
      
      toast({
        title: 'Saved',
        description: `${updateData.name} tier updated successfully`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update tier',
        variant: 'destructive'
      });
    } finally {
      setSavingTier(false);
    }
  };

  const handleCreateDiscount = async () => {
    if (!newDiscount.code.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a discount code',
        variant: 'destructive'
      });
      return;
    }

    const discountValue = newDiscount.discountType === 'percent'
      ? parseFloat(newDiscount.discount_percent)
      : parseFloat(newDiscount.discount_amount);
    if (!discountValue || discountValue <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a positive discount value',
        variant: 'destructive'
      });
      return;
    }
    if (newDiscount.discountType === 'percent' && discountValue > 100) {
      toast({
        title: 'Error',
        description: 'Percentage cannot exceed 100%',
        variant: 'destructive'
      });
      return;
    }

    try {
      setCreatingDiscount(true);
      
      const discountData = {
        code: newDiscount.code.toUpperCase(),
        applicable_tiers: newDiscount.applicable_tiers,
        new_members_only: newDiscount.new_members_only,
        monthly_only: newDiscount.monthly_only,
        is_active: true
      };

      if (newDiscount.discountType === 'percent' && newDiscount.discount_percent) {
        discountData.discount_percent = parseFloat(newDiscount.discount_percent);
      } else if (newDiscount.discountType === 'amount' && newDiscount.discount_amount) {
        discountData.discount_amount = parseFloat(newDiscount.discount_amount);
      }

      if (newDiscount.max_uses) {
        discountData.max_uses = parseInt(newDiscount.max_uses);
      }

      if (newDiscount.valid_until) {
        discountData.valid_until = new Date(newDiscount.valid_until).toISOString();
      }

      await createDiscountCode(discountData);
      
      toast({
        title: 'Success',
        description: `Discount code ${discountData.code} created`
      });

      // Reset form and reload
      setNewDiscount({
        code: '',
        discount_percent: '',
        discount_amount: '',
        discountType: 'percent',
        applicable_tiers: ['bronze', 'silver', 'gold'],
        max_uses: '',
        valid_until: '',
        new_members_only: false,
        monthly_only: false
      });
      
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create discount code',
        variant: 'destructive'
      });
    } finally {
      setCreatingDiscount(false);
    }
  };

  const handleDeleteDiscount = async (code) => {
    if (!confirm(`Delete discount code ${code}?`)) return;
    
    try {
      await deleteDiscountCode(code);
      toast({
        title: 'Deleted',
        description: `Discount code ${code} has been deleted`
      });
      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete discount code',
        variant: 'destructive'
      });
    }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-red-500 font-typewriter">Access Denied</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-gray-100 py-8 px-4" data-testid="payment-admin-page">
      {/* Film grain overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-50"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-amber-500 hover:text-amber-400"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-headline text-3xl text-amber-500 tracking-wider">PAYMENT MANAGEMENT</h1>
              <p className="text-gray-400 font-typewriter">Manage pricing, discounts, and transactions</p>
            </div>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="border-amber-900 text-amber-500 hover:bg-amber-900/20"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-900/90 border-2 border-green-900/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 font-typewriter text-sm">Total Revenue</p>
                  <p className="text-2xl font-headline text-green-500">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 font-typewriter text-sm">Transactions</p>
                  <p className="text-2xl font-headline text-amber-500">{stats?.total_transactions || 0}</p>
                </div>
                <CreditCard className="w-8 h-8 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/90 border-2 border-blue-900/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 font-typewriter text-sm">Subscriptions</p>
                  <p className="text-2xl font-headline text-blue-500">{stats?.subscriptions_sold || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/90 border-2 border-purple-900/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 font-typewriter text-sm">Posts Sold</p>
                  <p className="text-2xl font-headline text-purple-500">{stats?.posts_sold || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="discounts" className="space-y-6">
          <TabsList className="bg-gray-900/90 border border-amber-900/30 p-1">
            <TabsTrigger value="discounts" className="font-headline tracking-wider data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-500">
              <Percent className="w-4 h-4 mr-2" />
              DISCOUNTS
            </TabsTrigger>
            <TabsTrigger value="transactions" className="font-headline tracking-wider data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-500">
              <CreditCard className="w-4 h-4 mr-2" />
              TRANSACTIONS
            </TabsTrigger>
            <TabsTrigger value="pricing" className="font-headline tracking-wider data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-500">
              <DollarSign className="w-4 h-4 mr-2" />
              TIER PRICING
            </TabsTrigger>
          </TabsList>

          {/* Discounts Tab */}
          <TabsContent value="discounts" className="space-y-6">
            {/* Create New Discount */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">
                  <Gift className="w-5 h-5 inline mr-2" />
                  CREATE DISCOUNT CODE
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Create promotional codes for subscribers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="font-headline text-sm text-amber-600">CODE</Label>
                    <Input
                      value={newDiscount.code}
                      onChange={(e) => setNewDiscount(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      placeholder="SAVE20"
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter uppercase"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-headline text-sm text-amber-600">DISCOUNT TYPE</Label>
                    <Select 
                      value={newDiscount.discountType}
                      onValueChange={(value) => setNewDiscount(prev => ({ ...prev, discountType: value }))}
                    >
                      <SelectTrigger className="bg-gray-800 border-amber-900/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-amber-900/50">
                        <SelectItem value="percent">Percentage Off</SelectItem>
                        <SelectItem value="amount">Fixed Amount Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-headline text-sm text-amber-600">
                      {newDiscount.discountType === 'percent' ? 'PERCENTAGE' : 'AMOUNT ($)'}
                    </Label>
                    <Input
                      type="number"
                      value={newDiscount.discountType === 'percent' ? newDiscount.discount_percent : newDiscount.discount_amount}
                      onChange={(e) => setNewDiscount(prev => ({ 
                        ...prev, 
                        [newDiscount.discountType === 'percent' ? 'discount_percent' : 'discount_amount']: e.target.value 
                      }))}
                      placeholder={newDiscount.discountType === 'percent' ? '20' : '5.00'}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-headline text-sm text-amber-600">MAX USES (optional)</Label>
                    <Input
                      type="number"
                      value={newDiscount.max_uses}
                      onChange={(e) => setNewDiscount(prev => ({ ...prev, max_uses: e.target.value }))}
                      placeholder="Unlimited"
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-headline text-sm text-amber-600">EXPIRES (optional)</Label>
                    <Input
                      type="date"
                      value={newDiscount.valid_until}
                      onChange={(e) => setNewDiscount(prev => ({ ...prev, valid_until: e.target.value }))}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-headline text-sm text-amber-600">APPLIES TO TIER</Label>
                    <Select
                      value={newDiscount.applicable_tiers.length > 1 ? 'all' : (newDiscount.applicable_tiers[0] || 'all')}
                      onValueChange={(value) => {
                        if (value === 'all') {
                          setNewDiscount(prev => ({ ...prev, applicable_tiers: ['bronze', 'silver', 'gold'] }));
                        } else {
                          setNewDiscount(prev => ({ ...prev, applicable_tiers: [value] }));
                        }
                      }}
                    >
                      <SelectTrigger className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter" data-testid="discount-tier-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tiers</SelectItem>
                        {Object.entries(tiers).map(([key, tier]) => (
                          key !== 'free' && (
                            <SelectItem key={key} value={key}>{tier.name || key}</SelectItem>
                          )
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-headline text-sm text-amber-600">ELIGIBILITY</Label>
                    <label className="flex items-center gap-2 h-10 px-3 bg-gray-800 border border-amber-900/50 rounded-md cursor-pointer" data-testid="new-members-only-toggle">
                      <input
                        type="checkbox"
                        checked={newDiscount.new_members_only}
                        onChange={(e) => setNewDiscount(prev => ({ ...prev, new_members_only: e.target.checked }))}
                        className="accent-amber-600 w-4 h-4"
                        data-testid="new-members-only-checkbox"
                      />
                      <span className="font-typewriter text-sm text-gray-300">Non-paying members only</span>
                    </label>
                    <label className="flex items-center gap-2 h-10 px-3 bg-gray-800 border border-amber-900/50 rounded-md cursor-pointer" data-testid="monthly-only-toggle">
                      <input
                        type="checkbox"
                        checked={newDiscount.monthly_only}
                        onChange={(e) => setNewDiscount(prev => ({ ...prev, monthly_only: e.target.checked }))}
                        className="accent-amber-600 w-4 h-4"
                        data-testid="monthly-only-checkbox"
                      />
                      <span className="font-typewriter text-sm text-gray-300">Monthly billing only</span>
                    </label>
                  </div>

                  <div className="flex items-end">
                    <Button
                      onClick={handleCreateDiscount}
                      disabled={creatingDiscount}
                      className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider w-full"
                    >
                      {creatingDiscount ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      CREATE CODE
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Active Discount Codes */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">ACTIVE DISCOUNT CODES</CardTitle>
              </CardHeader>
              <CardContent>
                {discountCodes.length === 0 ? (
                  <div className="text-center py-8">
                    <Tag className="w-12 h-12 text-amber-900/50 mx-auto mb-2" />
                    <p className="text-gray-500 font-typewriter">No discount codes created yet</p>
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-900/30 overflow-hidden">
                    <Table>
                      <TableHeader className="bg-amber-900/20">
                        <TableRow className="border-amber-900/30">
                          <TableHead className="font-headline text-amber-500">CODE</TableHead>
                          <TableHead className="font-headline text-amber-500">DISCOUNT</TableHead>
                          <TableHead className="font-headline text-amber-500">USES</TableHead>
                          <TableHead className="font-headline text-amber-500">EXPIRES</TableHead>
                          <TableHead className="font-headline text-amber-500">STATUS</TableHead>
                          <TableHead className="font-headline text-amber-500 text-right">ACTIONS</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {discountCodes.map((discount) => (
                          <TableRow key={discount.code} className="border-amber-900/30">
                            <TableCell className="font-mono text-amber-500 font-bold">
                              {discount.code}
                              {discount.new_members_only && (
                                <Badge className="ml-2 bg-purple-900/30 text-purple-400 text-[10px]">NEW MEMBERS</Badge>
                              )}
                              {discount.monthly_only && (
                                <Badge className="ml-2 bg-blue-900/30 text-blue-400 text-[10px]">MONTHLY ONLY</Badge>
                              )}
                            </TableCell>
                            <TableCell className="font-typewriter">
                              {discount.discount_percent 
                                ? `${discount.discount_percent}% off` 
                                : discount.discount_amount ? `$${discount.discount_amount} off` : '—'}
                            </TableCell>
                            <TableCell className="font-typewriter">
                              {discount.current_uses || 0} / {discount.max_uses || '∞'}
                            </TableCell>
                            <TableCell className="font-typewriter text-gray-400">
                              {discount.valid_until 
                                ? new Date(discount.valid_until).toLocaleDateString() 
                                : 'Never'}
                            </TableCell>
                            <TableCell>
                              <Badge className={discount.is_active ? 'bg-green-900/30 text-green-500' : 'bg-red-900/30 text-red-500'}>
                                {discount.is_active ? 'ACTIVE' : 'INACTIVE'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDiscount(discount.code)}
                                className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">RECENT TRANSACTIONS</CardTitle>
                    <CardDescription className="font-typewriter text-gray-400">
                      View all payment transactions
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <Button
                      onClick={handlePaypalSync}
                      disabled={paypalSyncing}
                      variant="outline"
                      className="border-amber-900/50 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider"
                      data-testid="paypal-sync-button"
                    >
                      {paypalSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      SYNC PAYPAL
                    </Button>
                    {syncProgress && (
                      <p className="text-xs text-amber-500/70 mt-1 font-typewriter" data-testid="paypal-sync-progress">{syncProgress}</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-amber-900/50 mx-auto mb-2" />
                    <p className="text-gray-500 font-typewriter">No transactions yet</p>
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-900/30 overflow-hidden">
                    <Table>
                      <TableHeader className="bg-amber-900/20">
                        <TableRow className="border-amber-900/30">
                          <TableHead className="font-headline text-amber-500">DATE</TableHead>
                          <TableHead className="font-headline text-amber-500">USER</TableHead>
                          <TableHead className="font-headline text-amber-500">TYPE</TableHead>
                          <TableHead className="font-headline text-amber-500">ITEM</TableHead>
                          <TableHead className="font-headline text-amber-500">AMOUNT</TableHead>
                          <TableHead className="font-headline text-amber-500">STATUS</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.slice(0, 50).map((tx, index) => (
                          <TableRow key={tx.session_id || index} className="border-amber-900/30">
                            <TableCell className="font-typewriter text-gray-400">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="font-typewriter">{tx.user_email}</TableCell>
                            <TableCell>
                              <Badge className="bg-amber-900/30 text-amber-500 font-headline">
                                {tx.type?.toUpperCase().replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-typewriter">
                              {tx.tier_id || tx.post_title || tx.collection_title || '-'}
                            </TableCell>
                            <TableCell className="font-headline text-green-500">
                              ${tx.amount?.toFixed(2)}
                              {tx.discount_code && (
                                <span className="block text-xs text-amber-500 font-typewriter">
                                  {tx.discount_percent}% off ({tx.discount_code})
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                tx.payment_status === 'paid' 
                                  ? 'bg-green-900/30 text-green-500' 
                                  : tx.payment_status === 'pending'
                                  ? 'bg-amber-900/30 text-amber-500'
                                  : 'bg-red-900/30 text-red-500'
                              }>
                                {tx.payment_status?.toUpperCase()}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tier Pricing Tab */}
          <TabsContent value="pricing">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">SUBSCRIPTION TIER MANAGEMENT</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Edit tier names, pricing, descriptions, and benefits
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(tiers).filter(([key]) => key !== 'free').map(([tierId, tier]) => (
                    <Card key={tierId} className={`bg-gray-800/50 border ${editingTier === tierId ? 'border-amber-500' : 'border-amber-900/30'}`}>
                      <CardContent className="p-6">
                        {editingTier === tierId ? (
                          /* Edit Mode */
                          <div className="space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <Badge className={`font-headline tracking-wider ${
                                tierId === 'bronze' ? 'bg-amber-700' :
                                tierId === 'silver' ? 'bg-gray-400 text-black' :
                                'bg-yellow-500 text-black'
                              }`}>
                                EDITING {tierId.toUpperCase()}
                              </Badge>
                              <div className="flex gap-2">
                                <Button
                                  onClick={handleSaveTier}
                                  disabled={savingTier}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  size="sm"
                                  data-testid={`save-tier-${tierId}`}
                                >
                                  {savingTier ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Save className="w-4 h-4 mr-1" />
                                  )}
                                  Save
                                </Button>
                                <Button
                                  onClick={cancelEditingTier}
                                  variant="outline"
                                  size="sm"
                                  className="border-gray-600 text-gray-400 hover:bg-gray-700"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-amber-500 font-headline tracking-wider">TIER NAME</Label>
                                <Input
                                  value={tierEdits.name}
                                  onChange={(e) => setTierEdits(prev => ({ ...prev, name: e.target.value }))}
                                  className="bg-gray-900 border-amber-900/50 focus:border-amber-600 mt-1"
                                  placeholder="e.g., Bronze"
                                  data-testid={`tier-name-input-${tierId}`}
                                />
                              </div>
                              <div>
                                <Label className="text-amber-500 font-headline tracking-wider">PRICE (USD/MONTH)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={tierEdits.price}
                                  onChange={(e) => setTierEdits(prev => ({ ...prev, price: e.target.value }))}
                                  className="bg-gray-900 border-amber-900/50 focus:border-amber-600 mt-1"
                                  placeholder="9.99"
                                  data-testid={`tier-price-input-${tierId}`}
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-amber-500 font-headline tracking-wider">DESCRIPTION</Label>
                              <Input
                                value={tierEdits.description}
                                onChange={(e) => setTierEdits(prev => ({ ...prev, description: e.target.value }))}
                                className="bg-gray-900 border-amber-900/50 focus:border-amber-600 mt-1"
                                placeholder="Brief description of this tier"
                                data-testid={`tier-description-input-${tierId}`}
                              />
                            </div>
                            
                            <div>
                              <Label className="text-amber-500 font-headline tracking-wider">BENEFITS (ONE PER LINE)</Label>
                              <Textarea
                                value={tierEdits.benefits}
                                onChange={(e) => setTierEdits(prev => ({ ...prev, benefits: e.target.value }))}
                                className="bg-gray-900 border-amber-900/50 focus:border-amber-600 mt-1 min-h-[120px] font-typewriter"
                                placeholder="Access to exclusive content&#10;Early updates&#10;Bronze badge on profile"
                                data-testid={`tier-benefits-input-${tierId}`}
                              />
                              <p className="text-xs text-gray-500 mt-1 font-typewriter">Enter each benefit on a new line</p>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <Badge className={`font-headline tracking-wider ${
                                  tierId === 'bronze' ? 'bg-amber-700' :
                                  tierId === 'silver' ? 'bg-gray-400 text-black' :
                                  'bg-yellow-500 text-black'
                                }`}>
                                  {tier.name}
                                </Badge>
                                <span className="text-2xl font-headline text-green-500">
                                  ${tier.price?.toFixed(2)}<span className="text-sm text-gray-500">/mo</span>
                                </span>
                              </div>
                              <Button
                                onClick={() => startEditingTier(tierId)}
                                variant="outline"
                                size="sm"
                                className="border-amber-700 text-amber-500 hover:bg-amber-900/20"
                                data-testid={`edit-tier-${tierId}`}
                              >
                                <Edit3 className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                            </div>
                            
                            <p className="text-sm text-gray-400 font-typewriter mb-4">{tier.description}</p>
                            
                            {tier.benefits && tier.benefits.length > 0 && (
                              <div className="border-t border-amber-900/30 pt-4">
                                <p className="text-xs text-amber-500 font-headline tracking-wider mb-2">BENEFITS</p>
                                <ul className="space-y-1">
                                  {tier.benefits.map((benefit, idx) => (
                                    <li key={`${tier.id}-benefit-${idx}`} className="flex items-center gap-2 text-sm text-gray-300 font-typewriter">
                                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                      {benefit}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                  <p className="text-amber-500 font-headline tracking-wider text-sm mb-2">HOW IT WORKS</p>
                  <p className="text-gray-400 text-sm font-typewriter">
                    Changes are saved to the database and take effect immediately. 
                    The Tiers page will display your updated pricing and benefits.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PaymentAdminPage;
